import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Radio, RefreshCw, Users, Clock4, Building2, AlertTriangle, Calendar } from "lucide-react";

const POLL_INTERVAL_MS = 15000;
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function LiveAttendance() {
  const { user } = useAuth();
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const isSuper = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [data, setData] = useState({ rows: [], present_count: 0, date: "", is_today: true });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState(null);

  const isToday = selectedDate === todayIso();

  // Load unit list (super_admin only — unit_manager is auto-scoped server-side)
  useEffect(() => {
    if (!isSuper) return;
    api.get("/units").then(({ data }) => setUnits(data || [])).catch(() => {});
  }, [isSuper]);

  // Poll every 15 seconds (only when viewing today; past dates fetch once)
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const params = {};
        if (isSuper && selectedUnit) params.unit_id = selectedUnit;
        if (!isToday) params.date = selectedDate;
        const { data } = await api.get("/biometric/live-today", { params });
        if (cancelled) return;
        setData(data);
        setLastRefresh(new Date());
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || "Failed to load live attendance");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    fetchOnce();
    // Only poll while viewing today; historical dates are static
    if (isToday) {
      const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
      return () => { cancelled = true; clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [isSuper, selectedUnit, selectedDate, isToday]);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) =>
      (r.employee_name || "").toLowerCase().includes(q)
      || (r.employee_code || "").toLowerCase().includes(q)
      || (r.punch_id || "").toLowerCase().includes(q)
    );
  }, [data, filter]);

  const unmatchedCount = data?.rows?.filter((r) => !r.employee_id).length || 0;

  return (
    <div data-testid="live-attendance-page" className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <Radio className="h-7 w-7 text-red-600 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 5E · Live</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Live Attendance
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Real-time biometric punches from Mantra BIOFACE devices.
            {" "}{isToday
              ? "Refreshes every 15s. Parallel to monthly Paytime upload."
              : `Historical view — ${selectedDate}. Read-only.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded px-2 py-1">
            <Calendar className="h-3.5 w-3.5 text-slate-500" />
            <input
              type="date"
              value={selectedDate}
              max={todayIso()}
              onChange={(e) => setSelectedDate(e.target.value || todayIso())}
              className="text-xs bg-transparent outline-none"
              data-testid="live-attendance-date"
            />
            {!isToday && (
              <button
                onClick={() => setSelectedDate(todayIso())}
                className="text-[10px] text-red-600 hover:underline ml-1"
                data-testid="reset-to-today">
                Today
              </button>
            )}
          </div>
          {lastRefresh && isToday && (
            <span className="text-[11px] text-slate-500 flex items-center gap-1">
              <RefreshCw className="h-3 w-3" /> Updated {lastRefresh.toLocaleTimeString("en-IN")}
            </span>
          )}
        </div>
      </div>

      {/* Unit filter — super_admin only; unit_manager is locked server-side */}
      {isSuper && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Building2 className="h-4 w-4" />
            Unit:
          </div>
          <Select value={selectedUnit || "__all__"} onValueChange={(v) => setSelectedUnit(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-48 h-9" data-testid="unit-filter">
              <SelectValue placeholder="All units" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Units</SelectItem>
              {units.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`unit-option-${u.id}`}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by name, code, or punch ID…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 max-w-sm h-9"
            data-testid="filter-input"
          />
        </div>
      )}
      {!isSuper && (
        <div className="flex items-center gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-xs text-slate-700 flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" />
            Locked to your unit (set by HR)
          </div>
          <Input
            placeholder="Filter by name, code, or punch ID…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 max-w-sm h-9"
            data-testid="filter-input"
          />
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <KPI label={isToday ? "Present Today" : `Present on ${selectedDate}`} value={data.present_count} icon={Users} tone="emerald" testid="kpi-present" />
        <KPI label="Active Punches" value={data.rows?.reduce((s, r) => s + (r.punch_count || 0), 0) || 0}
             icon={Clock4} tone="pink" testid="kpi-punches" />
        <KPI label="Unmatched IDs" value={unmatchedCount} icon={AlertTriangle}
             tone={unmatchedCount > 0 ? "amber" : "slate"} testid="kpi-unmatched" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800" data-testid="error-banner">
          {error}
        </div>
      )}

      {/* Live table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-left px-3 py-2" title="Mantra device numeric user ID (real-time biometric)">Bio Punch</th>
                  <th className="text-left px-3 py-2" title="Paytime CSV Punch ID (payroll source)">Pay Punch</th>
                  <th className="text-right px-3 py-2">First In</th>
                  <th className="text-right px-3 py-2">Last Out</th>
                  <th className="text-right px-3 py-2">Worked</th>
                  <th className="text-right px-3 py-2">Punches</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan="6" className="text-center py-10 text-slate-400">Loading…</td></tr>
                )}
                {!loading && filteredRows.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-10 text-slate-400">
                    {filter ? "No matches" : (isToday
                      ? "No punches yet today. Devices will appear as soon as employees punch in."
                      : `No biometric punches recorded on ${selectedDate}.`)}
                  </td></tr>
                )}
                {!loading && filteredRows.map((r) => {
                  // If first_in and last_out are the same (single punch), show blank for last_out
                  // so admin doesn't get the misleading impression of a full shift.
                  const sameInOut = r.first_in && r.last_out && r.first_in === r.last_out;
                  const lastOutDisplay = sameInOut ? "" : (r.last_out || "—");
                  const nextDayOut = !!r.last_out_next_day;
                  const workedDisplay = sameInOut
                    ? "—"
                    : `${Math.floor((r.worked_minutes || 0) / 60)}h ${(r.worked_minutes || 0) % 60}m`;
                  return (
                    <tr key={`${r.punch_id}_${r.date}`} className="hover:bg-red-50/40" data-testid={`row-${r.punch_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{r.employee_name || <span className="text-amber-700 italic">Unmatched</span>}</div>
                        <div className="text-[10px] text-slate-500">{r.employee_code || "—"}</div>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-emerald-700 font-semibold">{r.bio_punch_id ?? r.punch_id}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{r.paytime_punch_id || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">{r.first_in || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700"
                          title={sameInOut ? "Only one punch recorded — last-out hidden" : (nextDayOut ? "Night shift — punched out next morning" : "")}>
                        <span data-testid={`last-out-${r.punch_id}`}>{lastOutDisplay}</span>
                        {nextDayOut && lastOutDisplay && (
                          <span className="ml-1 inline-block rounded-sm bg-indigo-100 text-indigo-700 text-[9px] font-semibold px-1 py-[1px] align-middle"
                                data-testid={`next-day-chip-${r.punch_id}`}>
                            +1d
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-900">{workedDisplay}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">{r.punch_count || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <p className="text-[10px] text-slate-400 text-center">
        Data is parallel to monthly Paytime upload — payroll authority remains with Paytime.
      </p>
    </div>
  );
}

function KPI({ label, value, icon: Icon, tone, testid }) {
  const cls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    pink: "bg-red-50 border-red-200 text-red-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  }[tone] || "bg-slate-50 border-slate-200 text-slate-900";
  return (
    <div className={`border rounded px-3 py-2 ${cls}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-75">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-2xl font-bold mt-1 font-mono leading-tight" style={{ fontFamily: "Chivo" }}>
        {value ?? "—"}
      </div>
    </div>
  );
}
